import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, Modal, Form, Table, Spinner, Alert, Badge, Collapse } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PurchaseApproval, PurchaseRequest } from '../types';
import { API_URL } from '../config';
import useAlert from '../hooks/useAlert';
import { useAuth } from '../context/AuthContext';
import ApproverQuestionsForm, { ApproverAnswersState } from '../components/ApproverQuestionsForm';
import {
  buildApproverAnswersPayload,
  parseApproverQuestions,
  validateApproverAnswers,
} from '../utils/approverQuestions';
import './Tickets.css';
import './Dashboard.css';
import { WorkflowPriorityBadge, WorkflowStatusBadge } from '../components/workflow/WorkflowBadges';

interface CancelledApprovalNotice {
  purchase_request_id: number;
  public_id: string;
  title: string;
  message: string;
  cancelled_at?: string;
  /** Campos em que o usuário era aprovador (ex.: Aprovação CSI, Aprovação DPO). */
  approval_fields?: string[];
}

const CANCELLED_PAGE_SIZE = 3;

function dismissedStorageKey(userId: number | undefined): string {
  return `approvals_dismissed_cancelled_notices:${userId ?? 'anon'}`;
}

function loadDismissedIds(userId: number | undefined): Set<number> {
  try {
    const raw = localStorage.getItem(dismissedStorageKey(userId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.map((id) => Number(id)).filter((id) => Number.isFinite(id)));
  } catch {
    return new Set();
  }
}

function saveDismissedIds(userId: number | undefined, ids: Set<number>): void {
  try {
    localStorage.setItem(dismissedStorageKey(userId), JSON.stringify(Array.from(ids)));
  } catch {
    // ignore quota / private mode
  }
}

const Approvals: React.FC = () => {
  const [pendingRequests, setPendingRequests] = useState<PurchaseRequest[]>([]);
  const [cancelledNotices, setCancelledNotices] = useState<CancelledApprovalNotice[]>([]);
  const [dismissedCancelledIds, setDismissedCancelledIds] = useState<Set<number>>(new Set());
  const [cancelledSectionOpen, setCancelledSectionOpen] = useState(false);
  const [cancelledPage, setCancelledPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<PurchaseRequest | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [rejectionJustification, setRejectionJustification] = useState('');
  const [approvalInAction, setApprovalInAction] = useState<PurchaseApproval | null>(null);
  const [approverAnswers, setApproverAnswers] = useState<ApproverAnswersState>({});
  const [processing, setProcessing] = useState<number | null>(null);
  const navigate = useNavigate();
  const { showAlert, AlertComponent } = useAlert();
  const { user } = useAuth();

  useEffect(() => {
    setDismissedCancelledIds(loadDismissedIds(user?.id));
  }, [user?.id]);

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  const visibleCancelledNotices = useMemo(() => {
    return cancelledNotices.filter((notice) => !dismissedCancelledIds.has(notice.purchase_request_id));
  }, [cancelledNotices, dismissedCancelledIds]);

  const dismissedCancelledCount = useMemo(() => {
    return cancelledNotices.filter((notice) => dismissedCancelledIds.has(notice.purchase_request_id)).length;
  }, [cancelledNotices, dismissedCancelledIds]);

  const cancelledTotalPages = Math.max(1, Math.ceil(visibleCancelledNotices.length / CANCELLED_PAGE_SIZE) || 1);

  useEffect(() => {
    if (cancelledPage > cancelledTotalPages) {
      setCancelledPage(cancelledTotalPages);
    }
  }, [cancelledPage, cancelledTotalPages]);

  const displayedCancelledNotices = useMemo(() => {
    const start = (cancelledPage - 1) * CANCELLED_PAGE_SIZE;
    return visibleCancelledNotices.slice(start, start + CANCELLED_PAGE_SIZE);
  }, [visibleCancelledNotices, cancelledPage]);

  const fetchPendingApprovals = async () => {
    try {
      setLoading(true);
      const [pendingRes, cancelledRes] = await Promise.all([
        axios.get(`${API_URL}/api/purchases/approvals/pending`),
        axios
          .get(`${API_URL}/api/purchases/approvals/cancelled-notices`, {
            params: { days: 30, limit: 10 },
          })
          .catch(() => ({ data: [] })),
      ]);
      setPendingRequests(pendingRes.data);
      setCancelledNotices(Array.isArray(cancelledRes.data) ? cancelledRes.data : []);
      setCancelledPage(1);
    } catch (error: any) {
      console.error('Erro ao carregar aprovações pendentes:', error);
      showAlert({
        title: 'Erro',
        message: 'Não foi possível carregar as aprovações pendentes.',
        variant: 'danger'
      });
    } finally {
      setLoading(false);
    }
  };

  const dismissCancelledNotice = (purchaseRequestId: number) => {
    setDismissedCancelledIds((prev) => {
      const next = new Set(prev);
      next.add(purchaseRequestId);
      saveDismissedIds(user?.id, next);
      return next;
    });
  };

  const dismissAllCancelledNotices = () => {
    setDismissedCancelledIds((prev) => {
      const next = new Set(prev);
      visibleCancelledNotices.forEach((notice) => next.add(notice.purchase_request_id));
      saveDismissedIds(user?.id, next);
      return next;
    });
    setCancelledSectionOpen(false);
    setCancelledPage(1);
  };

  const restoreDismissedCancelledNotices = () => {
    setDismissedCancelledIds((prev) => {
      const next = new Set(prev);
      cancelledNotices.forEach((notice) => next.delete(notice.purchase_request_id));
      saveDismissedIds(user?.id, next);
      return next;
    });
    setCancelledSectionOpen(true);
    setCancelledPage(1);
  };

  /** Pendências do usuário nesta requisição — cada uma pode ter perguntas próprias. */
  const getMyPendingApprovals = (request: PurchaseRequest): PurchaseApproval[] =>
    (request.approvals || []).filter(
      (approval) => approval.approver_id === user?.id && approval.status === 'pendente'
    );

  const withQuestions = (approvals: PurchaseApproval[]): PurchaseApproval[] =>
    approvals.filter((approval) => parseApproverQuestions(approval.approver_questions).length > 0);

  /** Perguntas em várias pendências exigem decidir slot a slot, no detalhe da requisição. */
  const requireSlotBySlotDecision = (request: PurchaseRequest): boolean => {
    const pending = getMyPendingApprovals(request);
    if (withQuestions(pending).length === 0 || pending.length <= 1) return false;
    showAlert({
      title: 'Responda pelas aprovações',
      message:
        'Esta requisição tem mais de uma aprovação sua com perguntas adicionais. '
        + 'Abra os detalhes para responder e decidir uma aprovação por vez.',
      variant: 'warning',
    });
    navigate(`/purchases/${request.id}`);
    return true;
  };

  const handleApproveClick = (request: PurchaseRequest) => {
    if (requireSlotBySlotDecision(request)) return;
    const pendingWithQuestions = withQuestions(getMyPendingApprovals(request));
    if (pendingWithQuestions.length === 0) {
      handleApprove(request.id);
      return;
    }
    setSelectedRequest(request);
    setApprovalInAction(pendingWithQuestions[0]);
    setApproverAnswers({});
    setShowApproveModal(true);
  };

  const handleApprove = async (
    requestId: number,
    approval?: PurchaseApproval | null,
  ) => {
    const questions = parseApproverQuestions(approval?.approver_questions);
    if (questions.length > 0) {
      const validationError = validateApproverAnswers(questions, approverAnswers);
      if (validationError) {
        showAlert({ title: 'Atenção', message: validationError, variant: 'warning' });
        return;
      }
    }
    try {
      setProcessing(requestId);
      const payload: Record<string, unknown> = {};
      if (approval?.id) {
        payload.approval_id = approval.id;
      }
      if (questions.length > 0) {
        payload.answers = buildApproverAnswersPayload(questions, approverAnswers);
      }
      const response = await axios.post(`${API_URL}/api/purchases/${requestId}/approve`, payload);
      console.log('Resposta da aprovação:', response.data);
      setShowApproveModal(false);
      setApprovalInAction(null);
      setApproverAnswers({});
      if (response.status >= 200 && response.status < 300) {
        showAlert({
          title: 'Sucesso',
          message: 'Requisição aprovada com sucesso!',
          variant: 'success'
        });
        await fetchPendingApprovals();
      }
    } catch (error: any) {
      console.error('Erro completo na aprovação:', error);
      console.error('Status:', error.response?.status);
      console.error('Data:', error.response?.data);
      if (error.response?.status === 200 || (error.response?.status >= 200 && error.response?.status < 300)) {
        showAlert({
          title: 'Sucesso',
          message: 'Requisição aprovada com sucesso!',
          variant: 'success'
        });
        await fetchPendingApprovals();
      } else {
        showAlert({
          title: 'Erro',
          message: error.response?.data?.detail || 'Não foi possível aprovar a requisição.',
          variant: 'danger'
        });
      }
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectClick = (request: PurchaseRequest) => {
    if (requireSlotBySlotDecision(request)) return;
    setSelectedRequest(request);
    setApprovalInAction(withQuestions(getMyPendingApprovals(request))[0] || null);
    setRejectionJustification('');
    setApproverAnswers({});
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectionJustification.trim()) {
      showAlert({
        title: 'Atenção',
        message: 'Por favor, informe a justificativa da reprovação.',
        variant: 'warning'
      });
      return;
    }

    const questions = parseApproverQuestions(approvalInAction?.approver_questions);
    if (questions.length > 0) {
      const validationError = validateApproverAnswers(questions, approverAnswers);
      if (validationError) {
        showAlert({ title: 'Atenção', message: validationError, variant: 'warning' });
        return;
      }
    }

    try {
      setProcessing(selectedRequest.id);
      await axios.post(`${API_URL}/api/purchases/${selectedRequest.id}/reject`, {
        justification: rejectionJustification.trim(),
        ...(questions.length > 0
          ? {
              approval_id: approvalInAction?.id,
              answers: buildApproverAnswersPayload(questions, approverAnswers),
            }
          : {}),
      });
      showAlert({
        title: 'Sucesso',
        message: 'Requisição reprovada. O requisitante foi notificado.',
        variant: 'success'
      });
      setShowRejectModal(false);
      setSelectedRequest(null);
      setApprovalInAction(null);
      setApproverAnswers({});
      setRejectionJustification('');
      await fetchPendingApprovals();
    } catch (error: any) {
      console.error('Erro ao reprovar requisição:', error);
      showAlert({
        title: 'Erro',
        message: error.response?.data?.detail || 'Não foi possível reprovar a requisição.',
        variant: 'danger'
      });
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div className="dashboard-page purchases-page">
        <div className="dashboard-section-card card">
          <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Carregando...</span>
            </Spinner>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page purchases-page">
      <AlertComponent />
      <div className="dashboard-hero mb-3">
        <div className="d-flex justify-content-between align-items-center dashboard-header">
          <div>
            <h2 className="dashboard-title mb-1">Aprovações de Compras</h2>
            <small className="text-muted">
              Fila operacional das requisições aguardando decisão por perfil aprovador.
            </small>
          </div>
          <div className="dashboard-live-meta">
            <span className="dashboard-live-dot" aria-hidden></span>
            <small>{pendingRequests.length} pendente(s)</small>
          </div>
        </div>
      </div>

      <Card className="mb-4 dashboard-section-card">
        <Card.Header>
          <h4 className="mb-0">Aprovações Pendentes</h4>
        </Card.Header>
        <Card.Body>
          {pendingRequests.length === 0 ? (
            <Alert variant="info">
              Você não possui requisições pendentes de aprovação no momento.
            </Alert>
          ) : (
            <Table responsive striped hover className="dashboard-table align-middle mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Título</th>
                  <th>Criador</th>
                  <th>Status</th>
                  <th>Prioridade</th>
                  <th>Data de Criação</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pendingRequests.map((request) => (
                  <tr key={request.id}>
                    <td>#{request.public_id || request.id}</td>
                    <td>
                      <Button
                        variant="link"
                        className="p-0 text-start"
                        onClick={() => navigate(`/purchases/${request.id}`)}
                      >
                        {request.title}
                      </Button>
                    </td>
                    <td>{request.creator?.name || 'N/A'}</td>
                    <td><WorkflowStatusBadge status={request.status} area="purchases" /></td>
                    <td>
                      {request.priority ? (
                        <WorkflowPriorityBadge priority={request.priority} />
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>{formatDate(request.created_at)}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleApproveClick(request)}
                          disabled={processing === request.id}
                        >
                          {processing === request.id ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Aprovando...
                            </>
                          ) : (
                            'Aprovar'
                          )}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRejectClick(request)}
                          disabled={processing === request.id}
                        >
                          Reprovar
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigate(`/purchases/${request.id}`)}
                        >
                          Ver Detalhes
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {cancelledNotices.length > 0 && (
        <Card className="mb-4 dashboard-section-card">
          <Card.Header className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-link text-decoration-none p-0 text-start"
              onClick={() => setCancelledSectionOpen((open) => !open)}
              aria-expanded={cancelledSectionOpen}
            >
              <h4 className="mb-0 d-inline">
                Requisições canceladas{' '}
                <Badge bg="warning" text="dark">
                  {visibleCancelledNotices.length}
                </Badge>
                {dismissedCancelledCount > 0 && (
                  <Badge bg="secondary" className="ms-1">
                    {dismissedCancelledCount} dispensada(s)
                  </Badge>
                )}
              </h4>
              <small className="d-block text-muted mt-1">
                {cancelledSectionOpen
                  ? 'Ocultar avisos'
                  : 'Avisos secundários (últimos 30 dias) — expandir se precisar'}
              </small>
            </button>
            <div className="d-flex gap-2 flex-wrap">
              {dismissedCancelledCount > 0 && (
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={restoreDismissedCancelledNotices}
                >
                  Restaurar avisos
                </Button>
              )}
              {visibleCancelledNotices.length > 0 && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={dismissAllCancelledNotices}
                >
                  Dispensar todas
                </Button>
              )}
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setCancelledSectionOpen((open) => !open)}
              >
                {cancelledSectionOpen ? 'Recolher' : 'Expandir'}
              </Button>
            </div>
          </Card.Header>
          <Collapse in={cancelledSectionOpen}>
            <div>
              <Card.Body className="d-flex flex-column gap-2 pt-3">
                {visibleCancelledNotices.length === 0 ? (
                  <Alert variant="secondary" className="mb-0">
                    Todos os avisos desta lista foram dispensados.{' '}
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 align-baseline"
                      onClick={restoreDismissedCancelledNotices}
                    >
                      Restaurar avisos
                    </Button>{' '}
                    para vê-los novamente.
                  </Alert>
                ) : (
                  <>
                    {displayedCancelledNotices.map((notice) => (
                      <Alert key={notice.purchase_request_id} variant="warning" className="mb-0 py-2">
                        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                          <div className="flex-grow-1">
                            <strong>#{notice.public_id}</strong> — {notice.title}
                            <div className="mt-1 small">{notice.message}</div>
                            {Array.isArray(notice.approval_fields) && notice.approval_fields.length > 0 && (
                              <div className="mt-1">
                                <small className="text-muted">Campos: </small>
                                {notice.approval_fields.map((fieldLabel) => (
                                  <Badge key={fieldLabel} bg="secondary" className="me-1">
                                    {fieldLabel}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            {notice.cancelled_at && (
                              <small className="text-muted d-block mt-1">
                                {formatDate(notice.cancelled_at)}
                              </small>
                            )}
                          </div>
                          <div className="d-flex gap-2 flex-shrink-0">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => navigate(`/purchases/${notice.purchase_request_id}`)}
                            >
                              Ver detalhes
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => dismissCancelledNotice(notice.purchase_request_id)}
                            >
                              Dispensar
                            </Button>
                          </div>
                        </div>
                      </Alert>
                    ))}

                    {visibleCancelledNotices.length > CANCELLED_PAGE_SIZE && (
                      <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                        <small className="text-muted">
                          Página {cancelledPage} de {cancelledTotalPages} · {visibleCancelledNotices.length} aviso(s)
                        </small>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={cancelledPage <= 1}
                            onClick={() => setCancelledPage((page) => Math.max(1, page - 1))}
                          >
                            Anterior
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={cancelledPage >= cancelledTotalPages}
                            onClick={() => setCancelledPage((page) => Math.min(cancelledTotalPages, page + 1))}
                          >
                            Próxima
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </div>
          </Collapse>
        </Card>
      )}

      <Modal
        show={showApproveModal}
        onHide={() => {
          setShowApproveModal(false);
          setApprovalInAction(null);
          setApproverAnswers({});
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Aprovar Requisição</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <>
              <p>
                <strong>Requisição #{selectedRequest.public_id || selectedRequest.id}:</strong> {selectedRequest.title}
              </p>
              <p className="text-muted">
                Responda às perguntas abaixo para concluir a aprovação. As respostas ficam
                registradas junto da sua aprovação.
              </p>
              <ApproverQuestionsForm
                questions={parseApproverQuestions(approvalInAction?.approver_questions)}
                answers={approverAnswers}
                onChange={setApproverAnswers}
                disabled={processing === selectedRequest.id}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowApproveModal(false);
              setApprovalInAction(null);
              setApproverAnswers({});
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={() => selectedRequest && handleApprove(selectedRequest.id, approvalInAction)}
            disabled={processing === selectedRequest?.id}
          >
            {processing === selectedRequest?.id ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Aprovando...
              </>
            ) : (
              'Confirmar Aprovação'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reprovar Requisição</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <>
              <p>
                <strong>Requisição #{selectedRequest.public_id || selectedRequest.id}:</strong> {selectedRequest.title}
              </p>
              <p className="text-muted">
                Por favor, informe a justificativa para a reprovação desta requisição.
                O requisitante será notificado e poderá fazer as modificações necessárias.
              </p>
              <Form.Group className="mb-3">
                <Form.Label>Justificativa <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={rejectionJustification}
                  onChange={(e) => setRejectionJustification(e.target.value)}
                  placeholder="Descreva o motivo da reprovação..."
                  required
                />
              </Form.Group>
              <ApproverQuestionsForm
                questions={parseApproverQuestions(approvalInAction?.approver_questions)}
                answers={approverAnswers}
                onChange={setApproverAnswers}
                disabled={processing === selectedRequest.id}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleReject}
            disabled={!rejectionJustification.trim() || processing === selectedRequest?.id}
          >
            {processing === selectedRequest?.id ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Reprovar...
              </>
            ) : (
              'Confirmar Reprovação'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Approvals;
